import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Rating,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BlockIcon from '@mui/icons-material/Block'
import SendIcon from '@mui/icons-material/Send'
import DeleteIcon from '@mui/icons-material/Delete'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import {
  UserDto,
  UserRole,
  userService,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ALL_USER_ROLES } from '../utils/userRoles'
import { PropertyView, PropertySection, PropertyField } from './PropertyView'

export interface UserDetailDialogProps {
  open: boolean
  onClose: () => void
  userId?: string
  onActionComplete?: () => void
}

export default function UserDetailDialog({
  open,
  onClose,
  userId,
  onActionComplete,
}: UserDetailDialogProps) {
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  const [editMode, setEditMode] = useState(false)
  const [draft, setDraft] = useState<Partial<UserDto>>({})
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Admin "set password" sub-dialog
  const [pwDialogOpen, setPwDialogOpen] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwError, setPwError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setEditMode(false)
      setError(null)
      setSuccess(null)
      setPwDialogOpen(false)
      setNewPassword('')
      setConfirmPassword('')
      setPwError(null)
    }
  }, [open])

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => userService.getById(userId as string),
    enabled: open && !!userId,
  })

  useEffect(() => {
    if (user) setDraft({ ...user })
  }, [user])

  const flash = (kind: 'success' | 'error', message: string) => {
    if (kind === 'success') {
      setSuccess(message)
      setError(null)
    } else {
      setError(message)
      setSuccess(null)
    }
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['user', userId] })
    queryClient.invalidateQueries({ queryKey: ['users'] })
    onActionComplete?.()
  }

  const updateMutation = useMutation({
    mutationFn: () => userService.update(userId as string, draft),
    onSuccess: () => {
      flash('success', 'User updated')
      setEditMode(false)
      invalidate()
    },
    onError: (e: any) =>
      flash('error', e?.response?.data?.message || 'Failed to update user'),
  })

  const activateMutation = useMutation({
    mutationFn: () => userService.activate(userId as string),
    onSuccess: () => {
      flash('success', 'User activated')
      invalidate()
    },
    onError: (e: any) =>
      flash('error', e?.response?.data?.message || 'Failed to activate'),
  })

  const deactivateMutation = useMutation({
    mutationFn: () => userService.deactivate(userId as string),
    onSuccess: () => {
      flash('success', 'User deactivated')
      invalidate()
    },
    onError: (e: any) =>
      flash('error', e?.response?.data?.message || 'Failed to deactivate'),
  })

  const resendMutation = useMutation({
    mutationFn: () => userService.resendInvitation(userId as string),
    onSuccess: () => flash('success', 'Invitation re-sent'),
    onError: (e: any) =>
      flash('error', e?.response?.data?.message || 'Failed to resend invitation'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => userService.delete(userId as string),
    onSuccess: () => {
      flash('success', 'User deleted')
      invalidate()
      setTimeout(onClose, 600)
    },
    onError: (e: any) =>
      flash('error', e?.response?.data?.message || 'Failed to delete'),
  })

  const setPasswordMutation = useMutation({
    mutationFn: () => userService.setPassword(userId as string, newPassword),
    onSuccess: () => {
      setPwDialogOpen(false)
      setNewPassword('')
      setConfirmPassword('')
      setPwError(null)
      flash('success', 'Password set. Share it with the user so they can sign in.')
    },
    onError: (e: any) =>
      setPwError(e?.response?.data?.message || 'Failed to set password'),
  })

  const pwValid = newPassword.length >= 8 && newPassword === confirmPassword

  const statusColor = (s?: string) => {
    if (s === 'ACTIVE') return 'success' as const
    if (s === 'PENDING_APPROVAL') return 'warning' as const
    if (s === 'DEACTIVATED') return 'default' as const
    return 'default' as const
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isLoading
          ? 'Loading user…'
          : `User — ${user?.name || (user?.id ? user.id.substring(0, 8) + '…' : '')}`}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {user && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={getLocalizedLabel('userStatus', user.status) || user.status}
                color={statusColor(user.status)}
                size="small"
              />
              {user.lastLoginAt && (
                <Chip
                  label={`Last login ${new Date(user.lastLoginAt).toLocaleDateString()}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {typeof user.rating === 'number' && user.rating > 0 && (
                <Rating value={user.rating} readOnly size="small" precision={0.5} />
              )}
              {user.ratingCount ? (
                <Typography variant="caption" color="text.secondary">
                  ({user.ratingCount} ratings)
                </Typography>
              ) : null}
            </Stack>

            <Divider />

            {editMode ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Name"
                    value={draft.name ?? ''}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nickname"
                    value={draft.nickName ?? ''}
                    onChange={(e) => setDraft({ ...draft, nickName: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    value={draft.email ?? ''}
                    onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    value={draft.phone ?? ''}
                    onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                    fullWidth
                    size="small"
                    helperText="Israeli format: +972… or 0…"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Locale"
                    value={draft.locale ?? ''}
                    onChange={(e) => setDraft({ ...draft, locale: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Timezone"
                    value={draft.timezone ?? ''}
                    onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Roles</InputLabel>
                    <Select
                      multiple
                      value={draft.roles ?? []}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          roles: (typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : e.target.value) as UserRole[],
                        })
                      }
                      input={<OutlinedInput label="Roles" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip
                              key={value}
                              label={getLocalizedLabel('userRole', value) || value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {ALL_USER_ROLES.map((role) => (
                        <MenuItem key={role} value={role}>
                          {getLocalizedLabel('userRole', role) || role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Account ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {user.accountId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    User ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {user.id}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <PropertyView>
                <PropertySection title="Profile">
                  <PropertyField label="Name" value={user.name} />
                  <PropertyField label="Nickname" value={user.nickName} />
                  <PropertyField label="Email" value={user.email} />
                  <PropertyField label="Phone" value={user.phone} />
                  <PropertyField label="Locale" value={user.locale} />
                  <PropertyField label="Timezone" value={user.timezone} />
                  <PropertyField label="Roles" full>
                    {user.roles && user.roles.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.roles.map((role) => (
                          <Chip
                            key={role}
                            label={getLocalizedLabel('userRole', role) || role}
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </PropertyField>
                </PropertySection>
                <PropertySection title="Identity">
                  <PropertyField label="Account ID" value={user.accountId} mono copyable />
                  <PropertyField label="User ID" value={user.id} mono copyable />
                </PropertySection>
              </PropertyView>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && <Alert severity="success">{success}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        {user && (
          <>
            <Tooltip title="Resend the invitation email/SMS">
              <span>
                <Button
                  startIcon={<SendIcon />}
                  onClick={() => resendMutation.mutate()}
                  disabled={resendMutation.isPending}
                >
                  Resend Invitation
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Set a new password for this user (fallback for users who can't reset it themselves)">
              <span>
                <Button
                  startIcon={<VpnKeyIcon />}
                  onClick={() => {
                    setNewPassword('')
                    setConfirmPassword('')
                    setPwError(null)
                    setPwDialogOpen(true)
                  }}
                >
                  Set Password
                </Button>
              </span>
            </Tooltip>
            {user.status === 'ACTIVE' ? (
              <Button
                color="warning"
                startIcon={<BlockIcon />}
                onClick={() => deactivateMutation.mutate()}
                disabled={deactivateMutation.isPending}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => activateMutation.mutate()}
                disabled={activateMutation.isPending}
              >
                Activate
              </Button>
            )}
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                if (window.confirm('Delete this user? This cannot be undone.')) {
                  deleteMutation.mutate()
                }
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
            {!editMode ? (
              <Button variant="outlined" onClick={() => setEditMode(true)}>
                Edit
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => {
                    setEditMode(false)
                    setDraft({ ...user })
                  }}
                >
                  Cancel Edit
                </Button>
                <Button
                  variant="contained"
                  onClick={() => updateMutation.mutate()}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>

      <Dialog open={pwDialogOpen} onClose={() => setPwDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Password</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sets a new password directly for {user?.name || 'this user'}. Use this to unblock users who
              can't complete the forgot-password flow, then share the password with them. The user must
              already have set up their account (joined or accepted an invitation).
            </Typography>
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="new-password"
              helperText="Min 8 chars incl. upper, lower, number, and special (@$!%*?&)."
            />
            <TextField
              label="Confirm password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="new-password"
              error={!!confirmPassword && confirmPassword !== newPassword}
              helperText={
                !!confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ' '
              }
            />
            {pwError && (
              <Alert severity="error" onClose={() => setPwError(null)}>
                {pwError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<VpnKeyIcon />}
            onClick={() => setPasswordMutation.mutate()}
            disabled={!pwValid || setPasswordMutation.isPending}
          >
            {setPasswordMutation.isPending ? 'Setting…' : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
